import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_generation, use_slice_ml_asset, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { UTILS__ml_asset } from '@/utils/UTILS__ml_asset';
import { path_relative_to_absolute } from '@/utils/utils_files_client';
import { assert_array_includes, assert_hash_sha256_not_empty } from '@shared/asserts/utils_asserts';
import { ENUM__ml_asset_family } from '@shared/dto/+model/sd/_enums/ENUM__ml_asset_family';
import { DTO__ml_asset } from '@shared/dto/+model/sd/ml_asset/DTO__ml_asset';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { ENUM__generation_status } from '@shared/dto/generation/_enums/ENUM__generation_status';
import { FIELD_PRIMITIVE__string_hash_ml_asset } from '@shared/dto/generation/graph_invoke/_fields/_primitives/string/hash/FIELD_PRIMITIVE__string_hash_ml_asset';
import { INVOCATION_IMAGE__viewport } from '@shared/dto/generation/graph_invoke/_invocations/image/INVOCATION_IMAGE__viewport';
import { INVOCATION_SD__denoise_latents_base } from '@shared/dto/generation/graph_invoke/_invocations/sd/denoise/INVOCATION_SD__denoise_latents_base';
import { INVOCATION_SD__denoise_latents_pipeline } from '@shared/dto/generation/graph_invoke/_invocations/sd/denoise/INVOCATION_SD__denoise_latents_pipeline';
import { useMemo, useRef, useState } from 'react';
import { FN_IMAGE_MASK__is_partially_masked } from './_methods/FN_IMAGE_MASK__is_partially_masked';

export function WATCHER__unet_inpaint() {
  const { toast } = useToast();

  /*
   * STORE
   */

  const { generation_current } = use_slice_generation(['generation_current']);
  const { is_all_data_loaded, current_workspace_user } = use_slice_workspace(['is_all_data_loaded', 'current_workspace_user']);
  const { ml_assets_map } = use_slice_ml_asset(['ml_assets_map']);
  const { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);

  /*
   * STATE
   */

  const ref_is_loading = useRef(false);

  //todo: use enum
  const [is_image_partially_masked, set_is_image_partially_masked] = useState<'true' | 'false' | 'unknown'>('unknown');

  const invocation__denoise = useMemo(() => get_invocation_denoise(), [generation_current]);

  const is_unet_inpaint_selected = useMemo(() => get_is_unet_inpaint_selected(), [generation_current, ml_assets_map, invocation__denoise]);

  /*
   * EFFECTS
   */

  use_effect(() => {
    void update_is_image_partially_masked();
  }, [generation_current]);

  use_effect(() => {
    if (!is_all_data_loaded) return; //not loaded yet
    if (generation_current.is_stub()) return; //not loaded yet
    if (ref_is_loading.current) return;
    if (invocation__denoise.is_stub()) return;
    if (generation_current.status != ENUM__generation_status.ADJUSTING) return;
    if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return;
    if (is_image_partially_masked == 'unknown') return; //can't switch inpaint/non inpaint
    if (g_general__ui_disabled) return; //can't update
    if (!invocation__denoise.has_effect_on_generation_runtime(generation_current.tool.graph)) return;

    assert_array_includes(['true', 'false'], is_image_partially_masked);

    let need_select_inpaint = is_image_partially_masked == 'true';

    let { family } = invocation__denoise;
    let { graph } = generation_current.tool;

    let field_model = invocation__denoise.unet.model.find_source(graph);

    if (field_model.get_value().is_empty_hash_sha256()) return;

    /*
     * UNET VALUE
     */

    let unet__value_new = get_unet_inverted_for(field_model.get_value(), need_select_inpaint, family, []);

    /*
     * UNET VALUE RANGE
     */
    let unet__values_allowed = field_model.get_values_allowed();

    let unet__value_range = field_model.get_value_range();
    let unet__value_range_map_new = new MAP__serializable<string, DTO__ml_asset>();

    for (let hash_sha256 of unet__value_range) {
      let unet_inverted = get_unet_inverted_for(hash_sha256, need_select_inpaint, family, unet__value_range_map_new.keys());
      if (unet_inverted.is_stub()) continue;

      unet__value_range_map_new.set(unet_inverted.hash_sha256, unet_inverted);
    }

    let unet__value_range_new = unet__value_range_map_new.keys();
    unet__value_range_new.sort((a, b) => unet__values_allowed.indexOf(a) - unet__values_allowed.indexOf(b));

    /*
     *
     */

    void try_select_unet(unet__value_new, unet__value_range_new);
    //todo: deselect all unnesessary
    //todo: try select inverted to unselected
  }, [
    is_all_data_loaded,
    generation_current,
    is_image_partially_masked,
    is_unet_inpaint_selected,
    invocation__denoise,
    ml_assets_map,
    current_workspace_user,
    g_general__ui_disabled,
  ]);

  /*
   * METHODS
   */

  function get_unet_inverted_for(hash_sha256: string, need_select_inpaint: boolean, family: ENUM__ml_asset_family, except_hashes: string[]) {
    assert_hash_sha256_not_empty(hash_sha256);

    let [name, version] = UTILS__ml_asset.get_name_and_version(hash_sha256);

    //1. try select by same family + name + version
    let out = UTILS__ml_asset.get_unet__by_family_name_version_inpaint(family, name, version, need_select_inpaint, except_hashes);
    //2. try select by same family + name
    if (out.is_stub()) out = UTILS__ml_asset.get_unet__by_family_name_inpaint(family, name, need_select_inpaint, except_hashes);
    //3. try select by same family
    if (out.is_stub()) out = UTILS__ml_asset.get_unet__by_family_inpaint(family, need_select_inpaint, except_hashes);

    return out;
  }

  function get_invocation_denoise() {
    let { graph } = generation_current.tool;

    let out = graph.nodes_map.values().find(node => node instanceof INVOCATION_SD__denoise_latents_base);

    if (!out) out = INVOCATION_SD__denoise_latents_pipeline.CREATE_STUB();

    return out;
  }

  function get_is_unet_inpaint_selected() {
    if (invocation__denoise.is_stub()) return false;

    let { graph } = generation_current.tool;
    let field_model = invocation__denoise.unet.model.find_source(graph);

    let unet_hash = field_model.get_value();

    let ml_asset = UTILS__ml_asset.get_by_hash(unet_hash);

    if (ml_asset.is_stub()) return false;

    return ml_asset.config.get_unet_is_inpaint();
  }

  async function update_is_image_partially_masked(): Promise<void> {
    let { graph } = generation_current.tool;

    let invocation_crop_and_mask = graph.nodes_map.values().find(node => node instanceof INVOCATION_IMAGE__viewport);

    if (!invocation_crop_and_mask) return set_is_image_partially_masked('unknown');
    if (invocation_crop_and_mask.image_mask.asset.is_stub()) return set_is_image_partially_masked('unknown');

    let { x, y, width, height } = invocation_crop_and_mask.crop_box.crop_box;
    let { url_relative } = invocation_crop_and_mask.image_mask.asset;

    let url_absolute = path_relative_to_absolute(url_relative);

    let is_partially_masked = await FN_IMAGE_MASK__is_partially_masked(url_absolute, x, y, width, height);

    set_is_image_partially_masked(is_partially_masked ? 'true' : 'false');
  }

  async function try_select_unet(ml_asset: DTO__ml_asset, values_range_new: string[]) {
    if (ref_is_loading.current) return;

    let field_new = invocation__denoise.unet.model.clone();

    let is_updated = false;

    is_updated = try_update_unet_value_range(field_new, values_range_new) || is_updated;
    is_updated = try_update_unet_value(field_new, ml_asset) || is_updated;

    if (!is_updated) return; //nothing to update

    ref_is_loading.current = true;

    await UTILS__graph_data.update_field(generation_current.tool.graph, field_new);

    show_toast();

    ref_is_loading.current = false;
  }

  //return true if updated
  function try_update_unet_value(field: FIELD_PRIMITIVE__string_hash_ml_asset, ml_asset: DTO__ml_asset) {
    if (ml_asset.is_stub()) return false;
    if (invocation__denoise.unet.model.get_value() == ml_asset.hash_sha256) return false;

    field.set_value(ml_asset.hash_sha256);
    field.add_value_to_values_range(ml_asset.hash_sha256);

    return true;
  }

  function try_update_unet_value_range(field: FIELD_PRIMITIVE__string_hash_ml_asset, values_range_new: string[]) {
    let value_range_set_old = new Set(field.get_value_range());
    let value_range_set_new = new Set(values_range_new);

    if (value_range_set_old.is_equal(value_range_set_new)) return false;
    if (values_range_new.is_empty()) return false;

    field.set_value_range(values_range_new);

    return true;
  }

  function show_toast() {
    let title = 'UNET is updated';

    toast({ title });
  }

  return <></>;
}
